import { useEffect, useRef, useState } from "react";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { Pause, Play } from "lucide-react";
import { motionMedia, motionStatementV13 } from "../config/media";

const MUTED = "#C8C5BB";
const INK = "#171713";

const Word = ({ word, start, end, progress }) => {
  const color = useTransform(progress, [start, end], [MUTED, INK]);
  return (
    <motion.span style={{ color }} className="inline-block">
      {word}
    </motion.span>
  );
};

// V1.3 Refinement 10 — "In Motion" (V1.3 ONLY).
// Beat 1: statement lines with scroll-linked word fill (no pin).
// Beat 2: video that expands from an inset frame to full-bleed 100vh as it enters.
export const MotionSectionV13 = () => {
  const statementRef = useRef(null);
  const videoWrapRef = useRef(null);
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [reducedMotion, setReducedMotion] = useState(false);
  const videoInView = useInView(videoWrapRef, { amount: 0.35 });

  const { scrollYProgress: fill } = useScroll({
    target: statementRef,
    offset: ["start 80%", "end 45%"],
  });

  const { scrollYProgress: grow } = useScroll({
    target: videoWrapRef,
    offset: ["start 95%", "start 8%"],
  });
  const width = useTransform(grow, [0, 1], ["86%", "100%"]);
  const height = useTransform(grow, [0, 1], ["72vh", "100vh"]);
  const radius = useTransform(grow, [0, 1], [32, 0]);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(mq.matches);
  }, []);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || reducedMotion) return;
    if (videoInView && playing) v.play().catch(() => {});
    else v.pause();
  }, [videoInView, playing, reducedMotion]);

  const words = motionStatementV13.lines.flatMap((line, li) =>
    line.split(" ").map((w, wi) => ({ w, key: `${li}-${wi}`, li }))
  );
  const n = words.length;

  return (
    <section data-testid="motion-section" className="bg-[var(--strollee-bg)]">
      {/* BEAT 1 — statement */}
      <div
        ref={statementRef}
        data-testid="motion-statement"
        className="flex min-h-[84vh] items-center justify-center px-10"
      >
        <h2 className="max-w-[1280px] text-center text-[52px] font-semibold leading-[1.06] tracking-[-0.035em] lg:text-[76px]">
          {motionStatementV13.lines.map((line, li) => (
            <span key={line} className="block">
              {words
                .filter((x) => x.li === li)
                .map((x) => {
                  const idx = words.findIndex((y) => y.key === x.key);
                  return (
                    <span key={x.key} className="inline-block whitespace-pre">
                      <Word
                        word={x.w}
                        progress={fill}
                        start={idx / n}
                        end={Math.min(1, (idx + 1.4) / n)}
                      />{" "}
                    </span>
                  );
                })}
            </span>
          ))}
        </h2>
      </div>

      {/* BEAT 2 — expanding video */}
      <div ref={videoWrapRef} className="flex justify-center pb-0">
        <motion.div
          data-testid="motion-video-frame"
          style={{ width, height, borderRadius: radius }}
          className="relative overflow-hidden bg-[var(--strollee-dark)]"
        >
          <video
            ref={videoRef}
            data-testid="motion-video"
            src={motionMedia.videoUrl}
            poster={motionMedia.posterUrl}
            aria-label={motionMedia.alt}
            muted
            loop
            playsInline
            preload="metadata"
            autoPlay={!reducedMotion}
            className="absolute inset-0 h-full w-full object-cover"
          />
          {!reducedMotion && (
            <button
              type="button"
              onClick={() => setPlaying((p) => !p)}
              aria-label={playing ? "Pause video" : "Play video"}
              data-testid="motion-playpause-btn"
              className="absolute bottom-8 right-8 flex h-11 w-11 items-center justify-center rounded-full border border-white/30 bg-white/10 text-white backdrop-blur-md transition-all duration-200 hover:bg-white hover:text-[var(--strollee-ink)] lg:bottom-10 lg:right-10"
            >
              {playing ? (
                <Pause size={16} strokeWidth={2} />
              ) : (
                <Play size={16} strokeWidth={2} className="ml-[2px]" />
              )}
            </button>
          )}
        </motion.div>
      </div>
    </section>
  );
};
